import React, { useEffect, useMemo, useState } from "react";
import { IAdmin } from "../IAdmin";
import MaterialReactTable, { MRT_ColumnDef } from "material-react-table";
import { DAYJS_DISPLAY_FORMAT_TABLES } from "../../../context/constant";
import GetActiveClientService from "../../../api/Client/GetActiveClient/GetActiveClientService";
import toast, { Toaster } from "react-hot-toast";
import editTeamService from "../../../api/Team/EditTeam/editTeamService";
import { Link, useNavigate } from "react-router-dom";
import { IconButton, Paper, Tooltip, Typography, Dialog } from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import dayjs from "dayjs";
import ClientDetails from "./ViewClientDetails";
 
const Clients = () => {
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [adminData, setAdminData] = useState<IAdmin[]>();
  const [selectedAdmin, setSelectedAdmin] = useState<IAdmin | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
 
  const columns = useMemo<MRT_ColumnDef<IAdmin>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Full Name",
        size: 200,
      },
      {
        accessorKey: "role",
        header: "Role",
        size: 100,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
      },
      {
        accessorKey: "phoneNumber",
        header: "Phone Number",
        size: 150,
      },
      {
        accessorKey: "gender",
        header: "Gender",
        size: 100,
      },
      {
        accessorKey: "planName",
        header: "Plan",
        size: 100,
      },
      {
        accessorKey: "joiningDate",
        header: "Joining Date",
        size: 150,
        Cell: ({ cell }) =>
          dayjs(cell.getValue<string>()).format(DAYJS_DISPLAY_FORMAT_TABLES),
      },
      {
        accessorKey: "createdOn",
        header: "Created On",
        size: 150,
        Cell: ({ cell }) =>
          dayjs(cell.getValue<string>()).format(DAYJS_DISPLAY_FORMAT_TABLES),
      },
      {
        header: "Status",
        accessorKey: "isActive",
        size: 50,
        Cell: ({ cell }) => {
          const value = cell.getValue<boolean>();
          return value ? (
            <CheckCircleOutlineIcon color="success" />
          ) : (
            <CancelOutlinedIcon color="error" />
          );
        },
      },
    ],
    []
  );
 
  const navigate = useNavigate();
 
  const fetchData = async () => {
    try {
      setIsLoading(true);
      const res = await GetActiveClientService();
      setAdminData(res.data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };
 
  useEffect(() => {
    fetchData();
  }, []);
 
  const changeStatus = async (row: IAdmin) => {
    try {
      const team = new FormData();
      team.append("isActive", String(!row.isActive));
      await editTeamService({ team, teamId: row._id });
      fetchData();
      navigate("/super-admin/client/new-rq");
    } catch (error: any) {
      toast.error(error.message);
    }
  };
 
  const handleViewDetails = (admin: IAdmin) => {
    setSelectedAdmin(admin);
    setIsDialogOpen(true);
  };
 
  return (
    <div className="bg-blue-200 md:p-7 p-2">
      <Paper elevation={3} style={{ padding: 30 }}>
        <Typography variant="h5">Admin Table</Typography>
        <MaterialReactTable
          state={{ isLoading, pagination }}
          columns={columns}
          data={adminData || []}
          enableRowActions
          positionActionsColumn="last"
          onPaginationChange={setPagination}
          autoResetPageIndex={false}
          renderRowActions={({ row }) => (
            <div style={{ display: "flex", gap: "10px" }}>
              <IconButton
                color="primary"
                onClick={() => handleViewDetails(row.original)}
              >
                <Tooltip title="View Details">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    className="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                  </svg>
                </Tooltip>
              </IconButton>
              <Tooltip title={"Change Status"}>
                <IconButton
                  color="primary"
                  aria-label={"Change Status"}
                  component="span"
                  onClick={() => changeStatus(row.original)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="size-5 text-addButton"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
                    />
                  </svg>
                </IconButton>
              </Tooltip>
            </div>
          )}
        />
      </Paper>
      <Dialog open={isDialogOpen} onClose={() => setIsDialogOpen(false)}>
        {selectedAdmin && <ClientDetails admin={selectedAdmin} />}
      </Dialog>
      <Toaster position="bottom-center" reverseOrder={false} />
    </div>
  );
};

export default Clients;
